import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CalendarDays, FileText } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { ProgramarVacaciones } from "@/componentes/talento/planificacion/ProgramarVacaciones"
import {
  ESTADO_PERIODO,
  type Periodo,
  type Planificacion,
  sePuedeAnular,
  sePuedeProgramar,
} from "@/componentes/talento/planificacion/tipos"
import { UtilidadesDePersona } from "@/componentes/talento/planificacion/UtilidadesDePersona"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha, hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * Vacaciones, utilidades y prestaciones de un trabajador, según su fecha de ingreso.
 *
 * ⚠ **Todo lo de aquí se DERIVA** de la fecha de ingreso y del salario legal del contrato
 * vigente; lo único guardado son las vacaciones que alguien programó. Corregir la fecha
 * de ingreso en la ficha corrige la planificación.
 *
 * ⚠ **Sin salario legal en bolívares se ven los días, no los montos**, y el aviso dice
 * por qué: un monto sobre un salario en dólares sería una cifra legal falsa.
 *
 * ⚠ **Fuera del `<form>` de la ficha**, como los contratos: programar se guarda solo.
 */
export function PlanificacionDePersona({ personaId }: { personaId: number }) {
  const cliente = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "th_calculo:administrar")
  const [programando, setProgramando] = useState<Periodo | null>(null)
  const [anulando, setAnulando] = useState<number | null>(null)
  const [motivo, setMotivo] = useState("")
  const clave = ["planificacion-laboral", personaId]

  const consulta = useQuery({
    queryKey: clave,
    queryFn: () => api.get<Planificacion>(`/api/v1/personas/${personaId}/planificacion-laboral`),
  })

  const refrescar = () => {
    cliente.invalidateQueries({ queryKey: clave })
    cliente.invalidateQueries({ queryKey: ["documentos-th"] })
  }

  const anular = useMutation({
    mutationFn: (id: number) => api.post(`/api/v1/vacaciones/${id}/anular`, { motivo }),
    onSuccess: () => {
      setAnulando(null)
      setMotivo("")
      refrescar()
    },
  })

  if (consulta.isPending) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Calculando vacaciones y beneficios…
      </p>
    )
  }
  if (consulta.isError) {
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudo cargar la planificación laboral.
      </p>
    )
  }
  const plan = consulta.data
  const hoy = hoyLocal()
  const error = anular.error

  return (
    <section aria-label="Vacaciones y beneficios" className="space-y-4">
      <h3 className="rotulo flex items-center gap-2">
        <CalendarDays size={16} aria-hidden={true} />
        Vacaciones y beneficios
      </h3>
      {plan.fecha_ingreso && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Ingresó el {comoFecha(plan.fecha_ingreso)} · {plan.antiguedad}
          {plan.salario_mensual && ` · salario legal Bs. ${monto(plan.salario_mensual)}`}
        </p>
      )}
      {plan.avisos.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm" style={{ color: "var(--warning)" }}>
          {plan.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}

      {plan.periodos.length > 0 && (
        <ul className="space-y-2">
          {plan.periodos.map((p) => (
            <li key={p.periodo} className="superficie space-y-1 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <strong className="tabular-nums">{p.texto}</strong>
                <Insignia tono={ESTADO_PERIODO[p.estado].tono}>
                  {ESTADO_PERIODO[p.estado].texto}
                </Insignia>
                <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {p.dias_habiles} días hábiles · {p.dias_bono} de bono · nace el{" "}
                  {comoFecha(p.nace)}
                </span>
              </div>
              {p.programacion && (
                <p className="tabular-nums">
                  Del {comoFecha(p.programacion.inicio)} al {comoFecha(p.programacion.ultimo_dia)},
                  reintegro el {comoFecha(p.programacion.reintegro)} · Bs.{" "}
                  {monto(p.programacion.total)}
                  {p.programacion.documento_th_id !== null && (
                    <Link
                      className="ml-2 inline-flex items-center gap-1 underline"
                      to={`/talento-humano/documento/${p.programacion.documento_th_id}`}
                    >
                      <FileText size={14} aria-hidden={true} /> Notificación
                    </Link>
                  )}
                </p>
              )}
              {puede && (
                <div className="flex flex-wrap items-center gap-2">
                  {sePuedeProgramar(p) && plan.salario_mensual && (
                    <Boton tamano="sm" onClick={() => setProgramando(p)}>
                      Programar
                    </Boton>
                  )}
                  {sePuedeAnular(p, hoy) &&
                    p.programacion &&
                    (anulando === p.programacion.id ? (
                      <>
                        <input
                          aria-label="Motivo de la anulación"
                          placeholder="Motivo (mínimo 10 caracteres)"
                          className="campo min-w-0 flex-1"
                          value={motivo}
                          onChange={(e) => setMotivo(e.target.value)}
                        />
                        <Boton tamano="sm" onClick={() => setAnulando(null)}>
                          No
                        </Boton>
                        <Boton
                          tamano="sm"
                          variante="primario"
                          disabled={motivo.trim().length < 10 || anular.isPending}
                          onClick={() => p.programacion && anular.mutate(p.programacion.id)}
                        >
                          Anular
                        </Boton>
                      </>
                    ) : (
                      <Boton tamano="sm" onClick={() => setAnulando(p.programacion?.id ?? null)}>
                        Anular programación
                      </Boton>
                    ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {plan.utilidades.length > 0 && (
        <UtilidadesDePersona
          personaId={personaId}
          utilidades={plan.utilidades}
          puede={puede}
          onCambio={refrescar}
        />
      )}

      {plan.garantia && (
        <div className="space-y-1 text-sm">
          <h4 className="font-medium">Garantía de prestaciones (art. 142)</h4>
          <p className="tabular-nums">
            Garantía: {plan.garantia.dias_trimestrales} días trimestrales +{" "}
            {plan.garantia.dias_adicionales} adicionales = {plan.garantia.dias_garantia} días
            {plan.garantia.monto_garantia && ` · Bs. ${monto(plan.garantia.monto_garantia)}`}
          </p>
          <p className="tabular-nums">
            Retroactivo: {plan.garantia.dias_retroactivo} días
            {plan.garantia.monto_retroactivo && ` · Bs. ${monto(plan.garantia.monto_retroactivo)}`}
          </p>
          {plan.garantia.monto_mayor && (
            <p className="tabular-nums font-medium">
              Se paga el mayor: Bs. {monto(plan.garantia.monto_mayor)}
            </p>
          )}
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Estimación al salario integral de hoy: sin el historial de salarios, cada trimestre se
            valora con el último.
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo completar"}
        </p>
      )}

      <ProgramarVacaciones
        personaId={personaId}
        periodo={programando}
        onCerrar={() => setProgramando(null)}
        onProgramada={refrescar}
      />
    </section>
  )
}
